import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingTablePage from "../LoadingTablePage";
import { FormCard, FormGrid } from "../../components/FormCard";
import { InputRow, SelectRow } from "../../components/Rows";
import { useAuth } from "../../providers/AuthContext";
import { createGridArea } from "../../api/GridCost";
import {
    GRID_PEAK_POWER_HIGHEST_HOUR,
    getGridPeakPowerOptions,
} from "../../components/gridCost/gridCostConstants";
import { useTranslation } from "react-i18next";

export default function CreateGridAreaPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getAccessToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [form, setForm] = useState({
        gridAreaCode: "",
        areaName: "",
        companyName: "",
        peakPowerMode: GRID_PEAK_POWER_HIGHEST_HOUR,
    });

    const onChange = (field, value) => {
        setHasChanges(true);
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const navigateBack = () => {
        navigate("/grid-areas");
    };

    const handleCreate = async () => {
        if (!form.gridAreaCode.trim() || !form.areaName.trim()) {
            toast.error(t("grid.codeAndNameRequired"));
            return;
        }

        const toastId = toast.loading(t("toast.creating", { entity: t("entity.gridArea") }));
        setLoading(true);
        try {
            const token = await getAccessToken();
            const created = await createGridArea(
                {
                    gridAreaCode: form.gridAreaCode.trim(),
                    areaName: form.areaName.trim(),
                    companyName: form.companyName.trim() || null,
                    peakPowerMode: form.peakPowerMode || GRID_PEAK_POWER_HIGHEST_HOUR,
                },
                token
            );
            toast.success(t("toast.created", { entity: t("entity.gridArea") }), { id: toastId });
            navigate(`/grid-areas/${created.gridAreaPublicId}`);
        } catch (err) {
            toast.error(err.message, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("entity.gridAreas")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button
                                type="button"
                                onClick={navigateBack}
                                className="text-black flex"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4">{t("pages.createGridArea")}</h2>
                            <div className="flex gap-4 ml-auto">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={navigateBack}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!hasChanges || loading}
                                    onClick={handleCreate}
                                >
                                    {t("common.create")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <FormCard>
                                <FormGrid>
                                    <InputRow
                                        label={t("form.gridAreaCode")}
                                        value={form.gridAreaCode}
                                        onChange={(value) => onChange("gridAreaCode", value)}
                                    />
                                    <InputRow
                                        label={t("common.name")}
                                        value={form.areaName}
                                        onChange={(value) => onChange("areaName", value)}
                                    />
                                    <InputRow
                                        label={t("form.companyOptional")}
                                        value={form.companyName}
                                        onChange={(value) => onChange("companyName", value)}
                                    />
                                    <SelectRow
                                        label={t("form.peakPower")}
                                        tooltip={t("grid.peakPowerTooltip")}
                                        value={form.peakPowerMode}
                                        options={getGridPeakPowerOptions(t)}
                                        onChange={(value) =>
                                            onChange(
                                                "peakPowerMode",
                                                value || GRID_PEAK_POWER_HIGHEST_HOUR
                                            )
                                        }
                                    />
                                </FormGrid>
                            </FormCard>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
